import cn from "classnames"
import { lazy, Suspense } from "react"

import { Card, Col, Descriptions, Flex, Row } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import {
  ExportOutlined,
  FolderOpenOutlined,
  SafetyCertificateOutlined
} from "@/icons"
import { AgentPlatformIcon } from "@/molecules/AgentPlatformIcon"
import { LinkButton } from "@/molecules/LinkButton"
import { useAgentById } from "@/organisms/Drawers/applications/ai-discovery/useAiDiscovery"
import { TABS } from "@/organisms/Drawers/components/AgentAiDiscoveryDrawer/const"
import { useActiveTab } from "@/organisms/Drawers/components/AgentAiDiscoveryDrawer/hooks/useActiveTab"
import { getAgentPlatformDetails } from "@/pages/DiscoveryPage/application/ai-discovery/useAiDiscovery.select"
import { ItemCardTemplate } from "../ItemCardTemplate"
import { ModelsCard, RelatedResourcesCard } from "../OverviewCards"

import styles from "./OverviewTabAgentAiDiscoveryDrawer.module.scss"

import type { TabComponentProps } from "@/organisms/Drawers/components/TabsDrawerTemplate"

const DataFlowGraph = lazy(() =>
  import("../DataFlowGraph").catch(() => ({
    default: () => <div>Failed to load Data Flow Graph</div>
  }))
)

export const OverviewTabAgentAiDiscoveryDrawer = (props: TabComponentProps) => {
  const { data, isLoading } = useAgentById(props)
  const { description, project, models, guardrails } = data || {}
  const resources = project?.resources
  const cardClassName = cn("card-agent-ai-discovery-drawer", styles.card)
  const columnCard = cn(cardClassName, !isLoading && styles.cardColumn)
  const itemCard = cn(columnCard, !isLoading && styles.cardItem)
  const { active } = useActiveTab(TABS.overview, data, props)
  const { t } = useTranslation()
  const { t: tAgents } = useTranslation("translation", {
    keyPrefix: "inventory.ai.agents"
  })
  const platform = getAgentPlatformDetails(data, t)
  // Only a platform agent's knowledge sources are listed here
  const knowledgeSources = platform.platformKey
    ? data?.knowledgeSources || []
    : []

  return (
    <Row gutter={[16, 16]} className="container-agent-ai-discovery-drawer">
      <Col span={24}>
        <Suspense
          fallback={
            <Card loading title="Data Flow" className={cardClassName} />
          }
        >
          <Card
            loading={isLoading}
            title="Data Flow"
            className={cn(cardClassName, !isLoading && styles.cardGraph)}
          >
            <div className={styles.graph}>
              {active && <DataFlowGraph key={data?.id} data={data} />}
            </div>
          </Card>
        </Suspense>
      </Col>
      <Col span={14}>
        <Flex vertical gap={16}>
          <Card
            loading={isLoading}
            title="Description"
            className={cn(columnCard, !isLoading && styles.cardDescription)}
          >
            <span className="sm-text-ks">{description}</span>
          </Card>
          <Card
            loading={isLoading}
            title="Guardrails"
            className={cn(columnCard, styles.cardGuardrails)}
          >
            {guardrails?.map(({ id, name, description, type }) => (
              <ItemCardTemplate
                key={id}
                title={
                  <Flex align="center" gap={8}>
                    <span
                      className={cn(
                        styles.guardrailText,
                        styles.guardrailTitle
                      )}
                    >
                      {name}
                    </span>
                    <span className={styles.guardrailText}>{`(${type})`}</span>
                  </Flex>
                }
                description={description}
                descriptionMaxRows={2}
                icon={<SafetyCertificateOutlined />}
              />
            ))}
          </Card>
        </Flex>
      </Col>
      <Col span={10}>
        <Flex vertical gap={16}>
          {platform.platformKey && (
            <Card
              loading={isLoading}
              title={
                <Flex align="center" gap={8}>
                  <AgentPlatformIcon platform={platform.platformKey} />
                  {platform.platformLabel}
                </Flex>
              }
              className={columnCard}
              extra={
                platform.consoleUrl && (
                  <LinkButton
                    type="link"
                    size="small"
                    icon={<ExportOutlined />}
                    href={platform.consoleUrl}
                    target="_blank"
                  >
                    {tAgents("openInConsole")}
                  </LinkButton>
                )
              }
            >
              <Descriptions
                flex
                items={[
                  {
                    label: tAgents("columns.owner"),
                    children: platform.owner
                  },
                  {
                    label: tAgents("columns.published"),
                    children: platform.published
                  },
                  {
                    label: tAgents("columns.auth"),
                    children: platform.authType
                  },
                  ...(platform.scopeName
                    ? [
                        {
                          label: tAgents("columns.source"),
                          children: platform.scopeName
                        }
                      ]
                    : [])
                ]}
              />
            </Card>
          )}
          <ModelsCard
            loading={isLoading}
            className={itemCard}
            models={models}
          />
          {knowledgeSources.length > 0 && (
            <Card
              loading={isLoading}
              title={t("inventory.ai.knowledgeSources.title")}
              className={itemCard}
            >
              {knowledgeSources.map(
                ({ id, name, kind, identifier, endpoint }) => (
                  <ItemCardTemplate
                    key={id}
                    title={name}
                    description={[
                      kind && t(`inventory.ai.knowledgeSources.kinds.${kind}`),
                      identifier || endpoint
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    icon={<FolderOpenOutlined />}
                  />
                )
              )}
            </Card>
          )}
          <RelatedResourcesCard
            loading={isLoading}
            className={itemCard}
            resources={resources}
          />
        </Flex>
      </Col>
    </Row>
  )
}
